"use client";

import { useState, useRef } from "react";
import Image from "next/image";

interface FeatureData {
  title: string;
  description: string;
  type: "image" | "video";
  path: string;
  format?: string;
  alt?: string;
  svg: React.ReactNode;
}

const features: FeatureData[] = [
  {
    title: "Zero-Based Budgeting",
    description:
      "Give every dollar a job! Our intuitive budgeting system helps you allocate every penny of your income to specific categories - whether it's bills, savings, or fun money. Watch your money work harder for you with real-time tracking and automatic calculations.",
    type: "image",
    path: "https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2970&q=80",
    alt: "Budget planning with calculator and notebook",
    svg: (
      <svg
        xmlns='http://www.w3.org/2000/svg'
        fill='none'
        viewBox='0 0 24 24'
        strokeWidth={1.5}
        stroke='currentColor'
        className='w-6 h-6'
      >
        <path
          strokeLinecap='round'
          strokeLinejoin='round'
          d='M2.25 18.75a60.07 60.07 0 0115.797 2.101c.727.198 1.453-.342 1.453-1.096V18.75M3.75 4.5v.75A.75.75 0 013 6h-.75m0 0v-.375c0-.621.504-1.125 1.125-1.125H20.25M2.25 6v9m18-10.5v.75c0 .414.336.75.75.75h.75m-1.5-1.5h.375c.621 0 1.125.504 1.125 1.125v9.75c0 .621-.504 1.125-1.125 1.125h-.375m1.5-1.5H21a.75.75 0 00-.75.75v.75m0 0H3.75m0 0h-.375a1.125 1.125 0 01-1.125-1.125V15m1.5 1.5v-.75A.75.75 0 003 15h-.75M15 10.5a3 3 0 11-6 0 3 3 0 016 0zm3 0h.008v.008H18V10.5zm-12 0h.008v.008H6V10.5z'
        />
      </svg>
    ),
  },
  {
    title: "Social Finance Community",
    description:
      "Connect with like-minded individuals on their financial journey. Share tips, celebrate victories, and learn from others' experiences. Join budget-specific groups, participate in challenges, and get motivated by your peers' success stories.",
    type: "image",
    path: "https://images.unsplash.com/photo-1521791136064-7986c2920216?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2969&q=80",
    alt: "People collaborating on finances",
    svg: (
      <svg
        xmlns='http://www.w3.org/2000/svg'
        fill='none'
        viewBox='0 0 24 24'
        strokeWidth={1.5}
        stroke='currentColor'
        className='w-6 h-6'
      >
        <path
          strokeLinecap='round'
          strokeLinejoin='round'
          d='M18 18.72a9.094 9.094 0 003.741-.479 3 3 0 00-4.682-2.72m.94 3.198l.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0112 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 016 18.719m12 0a5.971 5.971 0 00-.941-3.197m0 0A5.995 5.995 0 0012 12.75a5.995 5.995 0 00-5.058 2.772m0 0a3 3 0 00-4.681 2.72 8.986 8.986 0 003.74.477m.94-3.197a5.971 5.971 0 00-.94 3.197M15 6.75a3 3 0 11-6 0 3 3 0 016 0zm6 3a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0zm-13.5 0a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z'
        />
      </svg>
    ),
  },
  {
    title: "Goal Tracking & Challenges",
    description:
      "Set personal financial goals and track your progress. Join community challenges like 'No-Spend Week' or 'Save $1000 in 30 Days'. Compete with friends, earn badges, and celebrate milestones together.",
    type: "video",
    path: "https://cdn.coverr.co/videos/coverr-setting-up-goals-2527/1080p.mp4",
    format: "video/mp4",
    svg: (
      <svg
        xmlns='http://www.w3.org/2000/svg'
        fill='none'
        viewBox='0 0 24 24'
        strokeWidth={1.5}
        stroke='currentColor'
        className='w-6 h-6'
      >
        <path
          strokeLinecap='round'
          strokeLinejoin='round'
          d='M16.5 18.75h-9m9 0a3 3 0 013 3h-15a3 3 0 013-3m9 0v-3.375c0-.621-.503-1.125-1.125-1.125h-.871M7.5 18.75v-3.375c0-.621.504-1.125 1.125-1.125h.872m5.007 0H9.497m5.007 0a7.454 7.454 0 01-.982-3.172M9.497 14.25a7.454 7.454 0 00.981-3.172M5.25 4.236c-.982.143-1.954.317-2.916.52A6.003 6.003 0 007.73 9.728M5.25 4.236V4.5c0 2.108.966 3.99 2.48 5.228M5.25 4.236V2.721C7.456 2.41 9.71 2.25 12 2.25c2.291 0 4.545.16 6.75.47v1.516M7.73 9.728a6.726 6.726 0 002.748 1.35m8.272-6.842V4.5c0 2.108-.966 3.99-2.48 5.228m2.48-5.492a46.32 46.32 0 012.916.52 6.003 6.003 0 01-5.395 4.972m0 0a6.726 6.726 0 01-2.749 1.35m0 0a6.772 6.772 0 01-3.044 0'
        />
      </svg>
    ),
  },
  {
    title: "Smart Reports & Insights",
    description:
      "Visualize your spending patterns with beautiful charts and graphs. Get personalized insights based on your habits and compare anonymized data with similar households. Learn from the community's collective wisdom.",
    type: "image",
    path: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2970&q=80",
    alt: "Financial charts and analytics",
    svg: (
      <svg
        xmlns='http://www.w3.org/2000/svg'
        fill='none'
        viewBox='0 0 24 24'
        strokeWidth={1.5}
        stroke='currentColor'
        className='w-6 h-6'
      >
        <path
          strokeLinecap='round'
          strokeLinejoin='round'
          d='M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z'
        />
      </svg>
    ),
  },
  {
    title: "Debt Payoff Tools",
    description:
      "Take control of your debt with our powerful payoff tools. Compare different strategies like avalanche or snowball methods, track your progress, and connect with others who have successfully become debt-free. Share your journey and inspire others.",
    type: "video",
    path: "https://cdn.coverr.co/videos/coverr-calculating-finances-with-a-calculator-3654/1080p.mp4",
    format: "video/mp4",
    svg: (
      <svg
        xmlns='http://www.w3.org/2000/svg'
        fill='none'
        viewBox='0 0 24 24'
        strokeWidth={1.5}
        stroke='currentColor'
        className='w-6 h-6'
      >
        <path
          strokeLinecap='round'
          strokeLinejoin='round'
          d='M2.25 18L9 11.25l4.306 4.307a11.95 11.95 0 015.814-5.519l2.74-1.22m0 0l-5.94-2.28m5.94 2.28l-2.28 5.941'
        />
      </svg>
    ),
  },
];

interface ItemProps {
  feature: FeatureData;
  isOpen: boolean;
  setFeatureSelected: () => void;
}

const Item: React.FC<ItemProps> = ({ feature, isOpen, setFeatureSelected }) => {
  const accordion = useRef<HTMLDivElement>(null);
  const { title, description, svg } = feature;

  return (
    <li>
      <button
        className='relative text-bb-primary flex gap-2 items-center w-full py-5 text-base font-medium text-left md:text-lg'
        onClick={(e) => {
          e.preventDefault();
          setFeatureSelected();
        }}
        aria-expanded={isOpen}
      >
        <span className={`duration-100 ${isOpen ? "text-bb-primary" : ""}`}>
          {svg}
        </span>
        <span
          className={`flex-1 ${
            isOpen ? "text-bb-primary font-semibold" : "text-white"
          }`}
        >
          <h3 className='inline'>{title}</h3>
        </span>
      </button>

      <div
        ref={accordion}
        className={`transition-all duration-300 ease-in-out text-white/80 overflow-hidden`}
        style={
          isOpen
            ? { maxHeight: accordion?.current?.scrollHeight, opacity: 1 }
            : { maxHeight: 0, opacity: 0 }
        }
      >
        <div className='pb-5 leading-relaxed'>{description}</div>
      </div>
    </li>
  );
};

interface MediaProps {
  feature: FeatureData;
}

const Media: React.FC<MediaProps> = ({ feature }) => {
  const { type, path, format, alt } = feature;
  const style = "rounded-2xl aspect-square w-full sm:w-[26rem]";
  const size = {
    width: 500,
    height: 500,
  };

  if (type === "video") {
    return (
      <video
        className={style}
        autoPlay
        muted
        loop
        playsInline
        controls
        width={size.width}
        height={size.height}
      >
        <source src={path} type={format} />
      </video>
    );
  } else if (type === "image") {
    return (
      <Image
        src={path}
        alt={alt || "Feature image"}
        className={`${style} object-cover object-center`}
        width={size.width}
        height={size.height}
      />
    );
  } else {
    return <div className={`${style} !border-none`}></div>;
  }
};

const FeaturesAccordion: React.FC = () => {
  const [featureSelected, setFeatureSelected] = useState<number>(0);

  return (
    <section
      className='py-24 md:py-32 space-y-24 md:space-y-32 w-full mx-auto bg-gradient-to-b from-[#000001] to-[#1A1A1A] md:pl-8'
      id='features'
    >
      <div className='px-8 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-20'>
        <h2 className='font-extrabold text-4xl lg:text-6xl tracking-tight mb-12 md:mb-24 text-white'>
          All you need to change your finances
          <span className='bg-neutral text-neutral-content px-2 ml-1 md:ml-1.5 leading-relaxed whitespace-nowrap'>
            and be free
          </span>
        </h2>
        <div className=' flex flex-col md:flex-row gap-12 md:gap-24'>
          <div className='grid grid-cols-1 items-stretch gap-8 sm:gap-12 lg:grid-cols-2 lg:gap-20'>
            <ul className='w-full'>
              {features.map((feature, i) => (
                <Item
                  key={feature.title}
                  feature={feature}
                  isOpen={featureSelected === i}
                  setFeatureSelected={() => setFeatureSelected(i)}
                />
              ))}
            </ul>

            <Media feature={features[featureSelected]} key={featureSelected} />
          </div>
        </div>
      </div>
    </section>
  );
};

export default FeaturesAccordion;
